import { Link } from '@tanstack/react-router'
import { ChevronLeftIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib'

// Direct path, not the `@/shared/ui` barrel: the dock is mounted by the app
// layout on every route, and the barrel would pull the whole kit with it.
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

export type DockItem = {
  id: string
  icon: ReactNode
  /** The tile has no visible text: this names it, in the tooltip and for assistive tech. */
  label: string
  /**
   * Where the tile goes. Present makes it a real link — with an address in the
   * status bar, a middle click that opens a tab, and a name a screen reader
   * announces as somewhere to go rather than something to press. Absent leaves
   * it a button, which is right for a tile that opens a nested dock instead of
   * navigating.
   */
  to?: string
  /** Marks a section that opens a nested dock of its own. */
  hasNested?: boolean
  /** Draws the 26px hairline above this tile. */
  hasDividerBefore?: boolean
}

type DockProps = {
  items: DockItem[]
  activeId?: string
  /** The section the user came from — kept visible so the way back is one click. */
  memoId?: string
  onSelect?: (id: string) => void
  /** Present when showing a nested dock: renders the back tile above the list. */
  parent?: { icon: ReactNode; label: string }
  onBack?: () => void
  /** Mono caption under the rail: what is currently open. */
  openLabel?: string
  className?: string
}

// `press` carries the transition as well as the active state — see the utility
// in `src/index.css`. The tile is 40px, where the old 1px drop was invisible.
const TILE =
  'relative flex size-10 shrink-0 items-center justify-center rounded-xl border press [&_svg]:size-[17px]'

/**
 * Which of the three states a tile is wearing. Pulled out of the JSX because
 * two elements render it now — a link when the tile navigates, a button when it
 * opens a nested dock — and a tile that looked different depending on which it
 * was would be a bug nobody could name.
 */
const tileTone = (isActive: boolean, isMemo: boolean) =>
  cn(
    isActive &&
      'border-primary/35 bg-primary/16 text-primary inset-shadow-[0_1px_0_rgb(255_255_255/0.12)]',
    isMemo && 'border-border-strong bg-surface-2 text-fg-body',
    // The border is the half that reads: an idle tile is borderless, so one
    // appearing is a clearer answer than 5 rgb values of fill.
    !isActive &&
      !isMemo &&
      'border-transparent text-fg-secondary hover:border-border-strong hover:bg-surface-2 hover:text-foreground',
  )

/**
 * The floating rail: a glass capsule pinned to the left edge, vertically
 * centred, radius 20. It replaces a sidebar rather than sitting next to one —
 * page anatomy in this system is dock rail → content column → floating chrome,
 * and nothing else.
 *
 * Tiles carry no text, so the name arrives on hover: a tooltip out to the right
 * of the capsule, and the same string as the accessible name. It is the design
 * system's Tooltip rather than the native `title` — `title` waits about a
 * second, renders in the OS's own chrome, and never appears on keyboard focus.
 * The provider lives here rather than around the app shell, so the dock stays
 * drop-in; grouping matters on a rail, where sliding down the tiles should read
 * the next name at once instead of re-waiting the delay each time.
 */
export function Dock({
  items,
  activeId,
  memoId,
  onSelect,
  parent,
  onBack,
  openLabel,
  className,
}: DockProps) {
  const { t } = useTranslation('common')

  return (
    // 120ms rather than the wrapper's instant default: a tooltip that fires the
    // moment the cursor crosses the rail on its way elsewhere is a flicker.
    <TooltipProvider delay={120} closeDelay={0}>
    <div
      data-slot="dock"
      className={cn('sticky top-0 h-screen w-21.5 shrink-0 self-start', className)}
    >
      <div className="absolute top-1/2 left-[15px] flex w-14 -translate-y-1/2 flex-col items-center">
        <nav
          aria-label={t('ui.sections')}
          // Same fill and border as a card — the named steps, not an arbitrary
          // opacity: `bg-veil/[0.045]` compiles through oklab while
          // `bg-surface-1` stays plain rgba, so the two never matched exactly.
          // What separates the dock from a card is the blur and the shadow.
          className="flex flex-col items-center gap-1.5 rounded-[20px] border border-border bg-surface-1 px-2 py-2.5 backdrop-blur-[20px] shadow-dock"
        >
          {parent !== undefined && (
            <>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <button
                      type="button"
                      onClick={onBack}
                      aria-label={t('ui.allSections')}
                      className={cn(TILE, 'border-border bg-surface-2 text-fg-secondary')}
                    >
                      {parent.icon}
                      <span
                        aria-hidden
                        className="absolute -top-px -right-px flex size-3.5 items-center justify-center rounded-full border border-border-strong bg-overlay text-primary"
                      >
                        <ChevronLeftIcon strokeWidth={2.2} className="size-2" />
                      </span>
                    </button>
                  }
                />
                <TooltipContent side="right" sideOffset={12}>
                  {t('ui.allSections')}
                </TooltipContent>
              </Tooltip>
              <span aria-hidden className="mt-0.75 mb-0.25 h-px w-6.5 bg-border-strong" />
            </>
          )}

          {items.map((item) => {
            const isActive = item.id === activeId
            const isMemo = !isActive && item.id === memoId

            return (
              <div key={item.id} className="contents">
                {item.hasDividerBefore === true && (
                  <span aria-hidden className="my-0.75 h-px w-6.5 bg-border-strong" />
                )}
                <Tooltip>
                  <TooltipTrigger
                    render={
                      // A link when it has somewhere to go, a button otherwise.
                      // `onSelect` still fires for both: the layout uses it to
                      // remember where the reader came from.
                      item.to === undefined ? (
                        <button
                          type="button"
                          onClick={() => onSelect?.(item.id)}
                          aria-label={item.label}
                          aria-current={isActive ? 'page' : undefined}
                          className={cn(TILE, tileTone(isActive, isMemo))}
                        >
                          {item.icon}
                          {item.hasNested === true && (
                            <span
                              aria-hidden
                              className="absolute bottom-0.75 left-1/2 size-0.75 -translate-x-1/2 rounded-full bg-primary/70"
                            />
                          )}
                        </button>
                      ) : (
                        <Link
                          to={item.to}
                          onClick={() => onSelect?.(item.id)}
                          aria-label={item.label}
                          aria-current={isActive ? 'page' : undefined}
                          className={cn(TILE, tileTone(isActive, isMemo))}
                        >
                          {item.icon}
                          {item.hasNested === true && (
                            <span
                              aria-hidden
                              className="absolute bottom-0.75 left-1/2 size-0.75 -translate-x-1/2 rounded-full bg-primary/70"
                            />
                          )}
                        </Link>
                      )
                    }
                  />
                  <TooltipContent side="right" sideOffset={12}>
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              </div>
            )
          })}
        </nav>

        {openLabel !== undefined && (
          <div className="mt-2.25 max-w-20 text-center font-mono text-[9px] leading-[1.5] tracking-[0.05em] text-fg-disabled uppercase">
            {t('ui.opened')}
            <br />
            <span className="text-fg-secondary">{openLabel}</span>
          </div>
        )}
      </div>
    </div>
    </TooltipProvider>
  )
}
